import { ArrowRightIcon, GlobeAltIcon, KeyIcon, PencilIcon } from "@heroicons/react/24/outline";
import { Link } from "@inertiajs/react";
import Layout from "@/components/Layout";
import type { Persona } from "@/types";

type IndexProps = {
  personas: Persona[];
};

export default function Index({ personas }: IndexProps) {
  return (
    <Layout title="Manage Personas">
      <section className="card mb-6">
        <div className="card-body">
          <h2 className="mb-2 text-lg font-semibold">What are Personas?</h2>
          <p className="text-gray-600">
            Personas represent different types of users who might interact with your website. By testing with various
            personas, you can discover usability issues that might affect specific user groups.
          </p>
        </div>
      </section>

      <div className="mb-6 flex justify-end">
        <Link href="/personas/new" className="btn btn-primary">
          New Persona
        </Link>
      </div>

      {personas.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {personas.map((persona) => (
            <article key={persona.id} className="card relative transition hover:bg-gray-highlight">
              <Link
                href={`/personas/${persona.id}`}
                aria-label={`View details for ${persona.name}`}
                className="absolute inset-0"
              />
              <div className="card-body flex h-full flex-col justify-between">
                <div>
                  <div className="mb-4 flex items-start justify-between">
                    <h3 className="text-xl font-semibold">{persona.name}</h3>
                    <Link
                      href={`/personas/${persona.id}/edit?return_to=index`}
                      aria-label={`Edit ${persona.name}`}
                      className="z-10 text-primary hover:text-primary/90"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </Link>
                  </div>
                  <p className="mb-5 text-gray-600">{persona.description}</p>

                  <div className="mb-4 flex flex-col gap-5">
                    <Detail icon={GlobeAltIcon} label="Country" value={persona.country || "Not specified"} />
                    <Detail icon={KeyIcon} label="Authentication" value={authMethodLabel(persona.auth_method)} />
                  </div>
                </div>
                <div className="mt-4 flex justify-between">
                  <Link
                    href="/audits/new"
                    className="z-10 flex items-center text-sm font-medium text-primary hover:text-primary/90 hover:underline"
                  >
                    Use in audit
                    <ArrowRightIcon className="ml-2 h-4 w-4" />
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </Layout>
  );
}

function EmptyState() {
  return (
    <div className="card flex min-h-52 items-center justify-center">
      <div className="card-body text-center">
        <p className="mb-3 text-2xl font-semibold opacity-80">No personas yet</p>
        <p className="mx-auto mb-4 max-w-md text-lg opacity-60">
          Create your first persona to start simulating user experiences and uncover usability issues.
        </p>
        <Link href="/personas/new" className="btn btn-primary">
          New Persona
        </Link>
      </div>
    </div>
  );
}

function Detail({ icon: Icon, label, value }: { icon: typeof GlobeAltIcon; label: string; value: string }) {
  return (
    <div className="flex items-start">
      <div className="mt-0.5 w-8 shrink-0">
        <Icon className="h-5 w-5 text-gray-600" />
      </div>
      <div className="flex-1">
        <div className="mb-1 text-sm text-gray-500">{label}</div>
        <div className="font-medium">{value}</div>
      </div>
    </div>
  );
}

function authMethodLabel(method: Persona["auth_method"]) {
  if (method === "credentials") {
    return "Email & Password";
  }

  if (method === "instructions") {
    return "Custom";
  }

  return "None";
}
